import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { AlertTriangle, Filter } from 'lucide-react';

const ORIGIN_COLORS = {
  app: '#34d399',
  encoder: '#60a5fa',
  monitor: '#fbbf24',
  unknown: '#94a3b8',
};

const TYPE_COLORS = [
  '#34d399', '#60a5fa', '#fbbf24', '#f87171', '#a78bfa',
  '#22d3ee', '#fb923c', '#e879f9', '#4ade80', '#38bdf8',
];

const formatBucketLabel = (bucket) => {
  if (!bucket || bucket === 'unknown') return bucket || '';
  // YYYY-MM-DDTHH -> readable
  if (bucket.length >= 13 && bucket.includes('T')) {
    const [date, hour] = bucket.split('T');
    return `${date.slice(5)} ${hour}:00`;
  }
  return bucket;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="custom-chart-tooltip">
      <p className="label">{formatBucketLabel(label)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="value" style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export const ErrorsSection = ({
  data,
  loading,
  error,
  origin,
  errorType,
  sinceHours,
  onOriginChange,
  onErrorTypeChange,
  onSinceHoursChange,
  onRefresh,
}) => {
  const events = data?.events || [];
  const byOrigin = useMemo(() => data?.by_origin || {}, [data?.by_origin]);
  const byErrorType = useMemo(() => data?.by_error_type || {}, [data?.by_error_type]);
  const timeline = useMemo(() => data?.timeline || [], [data?.timeline]);

  const errorTypeOptions = useMemo(() => {
    return Object.keys(byErrorType).sort();
  }, [byErrorType]);

  const seriesKeys = useMemo(() => {
    if (origin === 'all') {
      return Object.keys(byOrigin);
    }
    // When filtering by origin, chart series by error_type
    return Object.keys(byErrorType);
  }, [origin, byOrigin, byErrorType]);

  const chartData = useMemo(() => {
    return timeline.map((row) => {
      const out = { time: row.time, total: row.total || 0 };
      if (origin === 'all') {
        Object.keys(byOrigin).forEach((o) => {
          let sum = 0;
          Object.keys(row).forEach((k) => {
            if (k.startsWith(`${o}:`)) sum += row[k] || 0;
          });
          out[o] = sum;
        });
      } else {
        Object.keys(byErrorType).forEach((t) => {
          out[t] = row[`${origin}:${t}`] || 0;
        });
      }
      return out;
    });
  }, [timeline, origin, byOrigin, byErrorType]);

  const selectStyle = {
    background: 'rgba(15, 23, 42, 0.9)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    color: '#fff',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '0.85rem',
  };

  return (
    <div className="glass-card" style={{ marginBottom: '24px', marginTop: '8px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#f87171',
          }}>
            <AlertTriangle size={22} />
          </div>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#ffffff' }}>
              Server Errors
            </h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              App playback, encoder pipeline, and live monitor
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Filter size={16} color="var(--text-muted)" />
          <select value={origin} onChange={(e) => onOriginChange(e.target.value)} style={selectStyle}>
            <option value="all">All origins</option>
            <option value="app">app</option>
            <option value="encoder">encoder</option>
            <option value="monitor">monitor</option>
          </select>
          <select value={errorType} onChange={(e) => onErrorTypeChange(e.target.value)} style={selectStyle}>
            <option value="all">All error types</option>
            {errorTypeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select value={sinceHours} onChange={(e) => onSinceHoursChange(Number(e.target.value))} style={selectStyle}>
            <option value={24}>Last 24h</option>
            <option value={168}>Last 7d</option>
            <option value={720}>Last 30d</option>
          </select>
          <button className="btn-emerald" onClick={onRefresh} style={{ padding: '8px 14px' }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Total: <strong style={{ color: '#fff' }}>{data?.total ?? 0}</strong>
        </div>
        {Object.entries(byOrigin).map(([o, count]) => (
          <div key={o} style={{
            fontSize: '0.8rem',
            padding: '4px 10px',
            borderRadius: '999px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            color: ORIGIN_COLORS[o] || '#fff',
          }}>
            {o}: {count}
          </div>
        ))}
      </div>

      {error && (
        <p style={{ color: '#fca5a5', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>
      )}

      {loading && !data ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Loading errors...</p>
      ) : (
        <>
          <div style={{ width: '100%', height: 280, marginBottom: '20px' }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(16, 185, 129, 0.1)" />
                <XAxis
                  dataKey="time"
                  tickFormatter={formatBucketLabel}
                  stroke="var(--text-muted)"
                  fontSize={11}
                />
                <YAxis allowDecimals={false} stroke="var(--text-muted)" fontSize={11} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {seriesKeys.map((key, i) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={key}
                    stackId="1"
                    stroke={ORIGIN_COLORS[key] || TYPE_COLORS[i % TYPE_COLORS.length]}
                    fill={ORIGIN_COLORS[key] || TYPE_COLORS[i % TYPE_COLORS.length]}
                    fillOpacity={0.35}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', textAlign: 'left', borderBottom: '1px solid rgba(16, 185, 129, 0.2)' }}>
                  <th style={{ padding: '8px 6px' }}>Time</th>
                  <th style={{ padding: '8px 6px' }}>Origin</th>
                  <th style={{ padding: '8px 6px' }}>Type</th>
                  <th style={{ padding: '8px 6px' }}>Message</th>
                  <th style={{ padding: '8px 6px' }}>Video</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 50).map((ev, idx) => (
                  <tr key={ev.id || idx} style={{ borderBottom: '1px solid rgba(16, 185, 129, 0.08)' }}>
                    <td style={{ padding: '8px 6px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '8px 6px', color: ORIGIN_COLORS[ev.origin] || '#fff' }}>
                      {ev.origin || '—'}
                    </td>
                    <td style={{ padding: '8px 6px', color: '#fff' }}>{ev.error_type || '—'}</td>
                    <td style={{ padding: '8px 6px', color: '#e2e8f0', maxWidth: 360 }}>
                      {(ev.error_message || '').slice(0, 160)}
                    </td>
                    <td style={{ padding: '8px 6px', color: 'var(--text-muted)' }}>
                      {ev.name || ev.video_id || '—'}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No errors in this range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
